import React, {createRef, useState, useContext, memo} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import {Box, Grid, Icon, MenuItem, Select, useTheme} from "@material-ui/core";
import MaterialTable from "@material-table/core";
import SystemContext from "../../../SystemContext";
import useDimensions from "../../../blocks/useDimensions";
import {defaultComponentsScroll, MaterialTableBody, FullWidthFilter} from "../../../controls/Material-Table-Components";
import {ColumnWidth } from "../../../blocks/ColumnHelper";
import { useCompanies, useAddCompany, useUpdateCompany, useDeleteCompany } from "../../../services/companies";
import moment from "moment";


const companyTypes = [
  {code: "ALL", value: "All Company Types"},
  {code: "FUEL_SUPPLIER", value: "Fuel Suppliers"},
  {code: "GSA", value: "General Sales Agents"},
  {code: "CLIENT", value: "Clients"},
  {code: "SUPPLIER", value: "Suppliers"},
  {code: "PERMIT_SUPPLIER", value: "Permit Suppliers"},
  {code: "GHA", value: "Ground Handlers"},
  {code: "CGHA", value: "Cargo Handlers"},
  {code: "AIRLINE", value: "Airlines"},
  {code: "SHIPPER", value: "Shippers"},
  {code: "CONSIGNEE", value: "Consignees"},
  {code: "FREESALE", value: "Freesale Client"},
  {code: "CONTRACT", value: "Awaiting Verification"},
];

const CompaniesList = (_props) => {
  const {isAuthenticated} = useAuth0();

  const props = {
    ..._props
  }
  const classes = props.classes;

  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    selectedIndex: null
  });
  const [companyType, setCompanyType] = useState("ALL");

  const handleRowClick = (event, rowData) => {
    if (eventBus) {
      eventBus.trigger('CompanyManagementList.rowSelected', rowData);
    }
  }

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddCompany = useAddCompany();
  const handleUpdateCompany = useUpdateCompany();
  const handleDeleteCompany = useDeleteCompany();

  const queryClient = useQueryClient();
  const extraKeys = companyType === "ALL" ? [] : companyType === "FREESALE" ? ["CLIENT"] : [companyType];

  const companies = useCompanies({queryClient, extraKeys});

  if (companies && companies.data && Array.isArray(companies.data)){
    companies['data'] = __.filter(companies.data, {isNew: false, isFreesale: (companyType === "FREESALE")});
  }

  const handleError = (errorObject) => {
    SystemData.alert("Error", `Failed to add the company record with the following error: ${errorObject.message}`);
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:13, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      filterCellStyle: { padding: "8px 2px" },
      editable: state.canEdit ? "onAdd" : "never",
    };

    return [
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */
      {
        ...baseColumn,
        ...ColumnWidth(50),
        filterCellStyle:{width: 20, padding: "8px 2px"},
        title: "KYC",
        field: "pioneerAccountId",
        filterComponent: FullWidthFilter,
        customFilterAndSearch: (term, rowData) => {
          let verified = false;
          if (rowData?.pioneerAccountId && rowData?.kycStatusCode && rowData?.kycExpiryDate) {
            if ([123340000, 418390003, 123340002, 418390002].includes(Number(rowData?.kycStatusCode))) {
              const expired = moment(rowData.kycExpiryDate).isBefore(moment());
              if (!expired)
                verified = true;
            }
          }
          const termUpper = `${term}`.toUpperCase();

          if (termUpper === "V" && verified) {
            return true;
          }
          if (termUpper === "U" && !verified) {
            return true;
          }
          return false;
        },
        render: rowData => {
          let verified = false;
          if (rowData?.pioneerAccountId && rowData?.kycStatusCode && rowData?.kycExpiryDate) {
            if ([123340000, 418390003, 123340002, 418390002].includes(Number(rowData?.kycStatusCode))) {
              const expired = moment(rowData.kycExpiryDate).isBefore(moment());
              if (!expired)
                verified = true;
            }
          }

          return (
            <Box
              component="img"
              sx={{
                height: 30,
                width: 35,
                // maxHeight: { xs: 233, md: 167 },
                // maxWidth: { xs: 350, md: 250 },
              }}
              alt="KYC"
              src={verified ? "images/KycVerified.png" : "images/KycUnverified.png"}
              style={{marginRight: 10, marginBottom: -5}}
            />
          )
        }
      },
      {
        ...baseColumn,
        cellStyle:{width: "100%"},
        filterCellStyle:{width: "100%", padding: "8px 2px"},
        title: "Name",
        field: "companyName",
        filterComponent: FullWidthFilter
      },
      {
        ...baseColumn,
        ...ColumnWidth(100),
        title: "Code",
        field: "companyCode",
        filterComponent: FullWidthFilter,
      },
      {
        ...baseColumn,
        ...ColumnWidth(70),
        title: "Branch",
        field: "branchCode",
        filterComponent: FullWidthFilter,
      },
    ];
  };

  const [tableContainerRef, {height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:40;
  const bodyHeight = tableHeight - thead;

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <div style={{background: "#ffffff", width: "calc(100% - 40px)", paddingTop: 16, paddingLeft: 20, paddingRight: 20}}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Select
              value={companyType}
              style={{width: "100%", textAlign: "alignLeft"}}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left"
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left"
                },
                getContentAnchorEl: null
              }}
              onChange={
                (evt, data) => {
                  const comboValue = __.find(companyTypes, {value: data.props.value});
                  if (comboValue.code !== companyType) {
                    setState(
                      {
                        ...state,
                        selectedIndex: undefined
                      });
                    if (eventBus) {
                      eventBus.trigger('CompanyManagementList.rowSelected', {});
                    }
                  }
                  setCompanyType(comboValue.code);
                }
              }
              renderValue={(value) => {
                const comboValue = __.find(companyTypes, {code: value});
                const valueToRender = (comboValue === undefined) ? "" : comboValue.value;
                return(`${valueToRender}`);
              }}
            >
              {(companyTypes) ?
                companyTypes.map((record) => {
                  return (
                    <MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>
                  );
                }) :
                null
              }
            </Select>
          </Grid>
        </Grid>
      </div>

      <div style={{background: "#ffffff", width: "calc(100% - 24px)", paddingTop: 16, paddingLeft: 20, paddingRight: 4}}>
        <div ref={tableContainerRef} className={state.canEdit?classes.companiesListEdit:classes.companiesList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 60}px)`}}>
          <MaterialTable
            icons={{ Filter: React.forwardRef((props, ref) => <Icon className="filterIcon">filter_list</Icon> )}}
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            onRowClick={handleRowClick}
            options={{
              tableRef,
              highlightRowStyle: {backgroundColor: "#F69259"},
              actionsColumnIndex: -99,
              cellStyle: {
                padding: "8px 2px",
              },
              headerRowStyle: {
                display: "table",
                width: "100%",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: 2,
              },
              draggable: false,
              toolbar: false,
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              padding: 'dense',
              paging: false,
              filtering: true,
              searchFieldAlignment: "left",
              addRowPosition: "first",
            }}
            components={{
              ...defaultComponentsScroll,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  rowHeight={40}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={bodyHeight}
                  headerHeight={thead}
                  filterRowHeight={46}
                />
              ),
            }}
            title="Companies List"
            columns={getColumns()}
            data={companies.data}
            editable={{
              isEditable: () => {
                return state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
              onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  handleAddCompany.mutate({record: newData, resolve, reject, extraKeys, postError: handleError});
                })
                : undefined,
              onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateCompany.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                }),
              onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteCompany.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
            }}
          />
        </div>
      </div>
    </div>
  );

}

export default memo(CompaniesList);
